import React from 'react';
import PropTypes from 'prop-types';

// Shared styles - use separate border properties to avoid React warnings
const baseInputStyle = {
  width: '100%',
  padding: '14px 16px',
  borderRadius: '12px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'rgba(148, 163, 184, 0.3)',
  background: 'rgba(255, 255, 255, 0.05)',
  color: '#e2e8f0',
  fontSize: '15px',
  outline: 'none',
  transition: 'all 0.2s ease',
  boxSizing: 'border-box',
};

const focusStyle = {
  borderColor: 'rgba(56, 189, 248, 0.6)',
  boxShadow: '0 0 0 3px rgba(56, 189, 248, 0.15)',
};

// Styled text input with floating label effect
export function FormInput({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  error,
  disabled = false,
  inputMode,
  maxLength,
  autoComplete,
  name,
}) {
  const [focused, setFocused] = React.useState(false);

  return (
    <div style={{ marginBottom: '20px' }}>
      <label
        style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '13px',
          fontWeight: 600,
          color: error ? '#fca5a5' : focused ? '#38bdf8' : '#94a3b8',
          transition: 'color 0.2s ease',
        }}
      >
        {label}
        {required && <span style={{ color: '#f87171', marginLeft: '4px' }}>*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        inputMode={inputMode}
        maxLength={maxLength}
        autoComplete={autoComplete}
        name={name}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...baseInputStyle,
          ...(focused ? focusStyle : {}),
          ...(error ? { borderColor: '#f87171' } : {}),
          ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
        }}
      />
      {error && (
        <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#fca5a5' }}>{error}</p>
      )}
    </div>
  );
}

FormInput.propTypes = {
  label: PropTypes.string.isRequired,
  type: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  inputMode: PropTypes.string,
  maxLength: PropTypes.number,
  autoComplete: PropTypes.string,
  name: PropTypes.string,
};

// SSN input with masking
export function SSNInput({ label, value, onChange, required = false, error }) {
  const [focused, setFocused] = React.useState(false);
  const [showFull, setShowFull] = React.useState(false);

  const formatSSN = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 9);
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  };

  const maskSSN = (val) => {
    const formatted = formatSSN(val);
    if (formatted.length <= 7) return formatted;
    return `***-**-${formatted.slice(-4)}`;
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <label
        style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '13px',
          fontWeight: 600,
          color: error ? '#fca5a5' : focused ? '#38bdf8' : '#94a3b8',
          transition: 'color 0.2s ease',
        }}
      >
        {label}
        {required && <span style={{ color: '#f87171', marginLeft: '4px' }}>*</span>}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          inputMode="numeric"
          value={focused || showFull ? formatSSN(value) : maskSSN(value)}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 9))}
          placeholder="XXX-XX-XXXX"
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            ...baseInputStyle,
            paddingRight: '48px',
            ...(focused ? focusStyle : {}),
            ...(error ? { borderColor: '#f87171' } : {}),
          }}
        />
        <button
          type="button"
          onClick={() => setShowFull(!showFull)}
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: '#64748b',
            cursor: 'pointer',
            fontSize: '12px',
          }}
        >
          {showFull ? 'Hide' : 'Show'}
        </button>
      </div>
      {error && (
        <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#fca5a5' }}>{error}</p>
      )}
    </div>
  );
}

SSNInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  error: PropTypes.string,
};

// Currency input with formatting
export function CurrencyInput({ label, value, onChange, required = false, error, placeholder = '$0' }) {
  const [focused, setFocused] = React.useState(false);

  const formatCurrency = (val) => {
    const num = parseFloat(val.replace(/[^\d.]/g, '')) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const handleChange = (e) => {
    const rawValue = e.target.value.replace(/[^\d.]/g, '');
    onChange(rawValue);
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <label
        style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '13px',
          fontWeight: 600,
          color: error ? '#fca5a5' : focused ? '#38bdf8' : '#94a3b8',
          transition: 'color 0.2s ease',
        }}
      >
        {label}
        {required && <span style={{ color: '#f87171', marginLeft: '4px' }}>*</span>}
      </label>
      <input
        type="text"
        inputMode="decimal"
        value={value ? formatCurrency(value) : ''}
        onChange={handleChange}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...baseInputStyle,
          ...(focused ? focusStyle : {}),
          ...(error ? { borderColor: '#f87171' } : {}),
        }}
      />
      {error && (
        <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#fca5a5' }}>{error}</p>
      )}
    </div>
  );
}

CurrencyInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  error: PropTypes.string,
  placeholder: PropTypes.string,
};

// Select dropdown
export function FormSelect({ label, value, onChange, options, required = false, error, placeholder }) {
  const [focused, setFocused] = React.useState(false);

  return (
    <div style={{ marginBottom: '20px' }}>
      <label
        style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '13px',
          fontWeight: 600,
          color: error ? '#fca5a5' : focused ? '#38bdf8' : '#94a3b8',
          transition: 'color 0.2s ease',
        }}
      >
        {label}
        {required && <span style={{ color: '#f87171', marginLeft: '4px' }}>*</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...baseInputStyle,
          cursor: 'pointer',
          appearance: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 12px center',
          backgroundSize: '18px',
          paddingRight: '44px',
          ...(focused ? focusStyle : {}),
          ...(error ? { borderColor: '#f87171' } : {}),
        }}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} style={{ background: '#1e293b', color: '#e2e8f0' }}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && (
        <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#fca5a5' }}>{error}</p>
      )}
    </div>
  );
}

FormSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  required: PropTypes.bool,
  error: PropTypes.string,
  placeholder: PropTypes.string,
};

// Card-style radio group for important selections
export function CardSelect({ label, value, onChange, options, required = false, columns = 3 }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <label
        style={{
          display: 'block',
          marginBottom: '12px',
          fontSize: '14px',
          fontWeight: 600,
          color: '#e2e8f0',
        }}
      >
        {label}
        {required && <span style={{ color: '#f87171', marginLeft: '4px' }}>*</span>}
      </label>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: '12px',
        }}
      >
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            style={{
              padding: '16px 12px',
              borderRadius: '12px',
              border:
                value === opt.value
                  ? '2px solid #38bdf8'
                  : '1px solid rgba(148, 163, 184, 0.3)',
              background:
                value === opt.value
                  ? 'linear-gradient(135deg, rgba(56, 189, 248, 0.15), rgba(99, 102, 241, 0.1))'
                  : 'rgba(255, 255, 255, 0.03)',
              color: value === opt.value ? '#e2e8f0' : '#94a3b8',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.2s ease',
              boxShadow: value === opt.value ? '0 4px 20px rgba(56, 189, 248, 0.2)' : 'none',
            }}
          >
            {opt.icon && (
              <div style={{ marginBottom: '8px', fontSize: '24px' }}>{opt.icon}</div>
            )}
            <div style={{ fontWeight: 600, fontSize: '14px' }}>{opt.label}</div>
            {opt.description && (
              <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.7 }}>
                {opt.description}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

CardSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      description: PropTypes.string,
    })
  ).isRequired,
  required: PropTypes.bool,
  columns: PropTypes.number,
};

// Date input
export function DateInput({ label, value, onChange, required = false, error, max, min }) {
  const [focused, setFocused] = React.useState(false);

  return (
    <div style={{ marginBottom: '20px' }}>
      <label
        style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '13px',
          fontWeight: 600,
          color: error ? '#fca5a5' : focused ? '#38bdf8' : '#94a3b8',
          transition: 'color 0.2s ease',
        }}
      >
        {label}
        {required && <span style={{ color: '#f87171', marginLeft: '4px' }}>*</span>}
      </label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        max={max}
        min={min}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...baseInputStyle,
          colorScheme: 'dark',
          ...(focused ? focusStyle : {}),
          ...(error ? { borderColor: '#f87171' } : {}),
        }}
      />
      {error && (
        <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#fca5a5' }}>{error}</p>
      )}
    </div>
  );
}

DateInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  error: PropTypes.string,
  max: PropTypes.string,
  min: PropTypes.string,
};

// Phone input with formatting
export function PhoneInput({ label, value, onChange, required = false, error }) {
  const [focused, setFocused] = React.useState(false);

  const formatPhone = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <label
        style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '13px',
          fontWeight: 600,
          color: error ? '#fca5a5' : focused ? '#38bdf8' : '#94a3b8',
          transition: 'color 0.2s ease',
        }}
      >
        {label}
        {required && <span style={{ color: '#f87171', marginLeft: '4px' }}>*</span>}
      </label>
      <input
        type="tel"
        inputMode="tel"
        value={formatPhone(value)}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
        placeholder="(555) 555-5555"
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...baseInputStyle,
          ...(focused ? focusStyle : {}),
          ...(error ? { borderColor: '#f87171' } : {}),
        }}
      />
      {error && (
        <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#fca5a5' }}>{error}</p>
      )}
    </div>
  );
}

PhoneInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  error: PropTypes.string,
};

// Section header for form sections
export function SectionHeader({ title, subtitle, icon }) {
  return (
    <div style={{ marginBottom: '28px', textAlign: 'center' }}>
      {icon && (
        <div
          style={{
            width: '56px',
            height: '56px',
            margin: '0 auto 16px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.2), rgba(99, 102, 241, 0.15))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#38bdf8',
          }}
        >
          {icon}
        </div>
      )}
      <h2
        style={{
          margin: '0 0 8px',
          fontSize: '24px',
          fontWeight: 700,
          color: '#e2e8f0',
        }}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          style={{
            margin: 0,
            fontSize: '14px',
            color: '#94a3b8',
            lineHeight: 1.5,
          }}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.node,
};

// Form row for side-by-side inputs
export function FormRow({ children }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px',
        marginBottom: '20px',
      }}
    >
      {children}
    </div>
  );
}

FormRow.propTypes = {
  children: PropTypes.node.isRequired,
};

// Progress indicator for form sections
export function FormProgress({ currentSection, totalSections, sectionNames }) {
  return (
    <div style={{ marginBottom: '32px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        {Array.from({ length: totalSections }, (_, i) => (
          <React.Fragment key={i}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background:
                    i < currentSection
                      ? 'linear-gradient(135deg, #22c55e, #16a34a)'
                      : i === currentSection
                      ? 'linear-gradient(135deg, #38bdf8, #6366f1)'
                      : 'rgba(148, 163, 184, 0.2)',
                  color: i <= currentSection ? '#fff' : '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 600,
                  boxShadow:
                    i === currentSection ? '0 0 12px rgba(56, 189, 248, 0.5)' : 'none',
                  transition: 'all 0.3s ease',
                }}
              >
                {i < currentSection ? '✓' : i + 1}
              </div>
              {sectionNames && (
                <span
                  style={{
                    fontSize: '10px',
                    color: i === currentSection ? '#e2e8f0' : '#64748b',
                    fontWeight: i === currentSection ? 600 : 400,
                    maxWidth: '60px',
                    textAlign: 'center',
                    lineHeight: 1.2,
                    display: 'none',
                  }}
                >
                  {sectionNames[i]}
                </span>
              )}
            </div>
            {i < totalSections - 1 && (
              <div
                style={{
                  width: '24px',
                  height: '2px',
                  background:
                    i < currentSection
                      ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                      : 'rgba(148, 163, 184, 0.2)',
                  transition: 'all 0.3s ease',
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      {sectionNames && (
        <p
          style={{
            textAlign: 'center',
            marginTop: '12px',
            fontSize: '13px',
            color: '#94a3b8',
          }}
        >
          Step {currentSection + 1} of {totalSections}: <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{sectionNames[currentSection]}</span>
        </p>
      )}
    </div>
  );
}

FormProgress.propTypes = {
  currentSection: PropTypes.number.isRequired,
  totalSections: PropTypes.number.isRequired,
  sectionNames: PropTypes.arrayOf(PropTypes.string),
};

// Form navigation buttons
export function FormNavigation({ onBack, onNext, onSkip, nextLabel = 'Continue', isFirstSection, isLastSection, isValid = true }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '32px',
        paddingTop: '24px',
        borderTop: '1px solid rgba(148, 163, 184, 0.15)',
      }}
    >
      <div>
        {!isFirstSection && (
          <button
            type="button"
            onClick={onBack}
            style={{
              padding: '12px 24px',
              borderRadius: '12px',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              background: 'transparent',
              color: '#94a3b8',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            Back
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: '12px' }}>
        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            style={{
              padding: '12px 24px',
              borderRadius: '12px',
              border: 'none',
              background: 'transparent',
              color: '#64748b',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Skip for now
          </button>
        )}
        <button
          type="button"
          onClick={onNext}
          disabled={!isValid}
          style={{
            padding: '12px 32px',
            borderRadius: '12px',
            border: 'none',
            background: isValid
              ? 'linear-gradient(135deg, #0ea5e9, #6366f1)'
              : 'rgba(148, 163, 184, 0.2)',
            color: isValid ? '#fff' : '#64748b',
            fontSize: '14px',
            fontWeight: 700,
            cursor: isValid ? 'pointer' : 'not-allowed',
            boxShadow: isValid ? '0 8px 24px rgba(14, 165, 233, 0.3)' : 'none',
            transition: 'all 0.2s ease',
          }}
        >
          {nextLabel}
        </button>
      </div>
    </div>
  );
}

FormNavigation.propTypes = {
  onBack: PropTypes.func,
  onNext: PropTypes.func.isRequired,
  onSkip: PropTypes.func,
  nextLabel: PropTypes.string,
  isFirstSection: PropTypes.bool,
  isLastSection: PropTypes.bool,
  isValid: PropTypes.bool,
};

// Text area for longer text input
export function FormTextArea({ label, value, onChange, placeholder, required = false, error, rows = 3 }) {
  const [focused, setFocused] = React.useState(false);

  return (
    <div style={{ marginBottom: '20px' }}>
      <label
        style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '13px',
          fontWeight: 600,
          color: error ? '#fca5a5' : focused ? '#38bdf8' : '#94a3b8',
          transition: 'color 0.2s ease',
        }}
      >
        {label}
        {required && <span style={{ color: '#f87171', marginLeft: '4px' }}>*</span>}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...baseInputStyle,
          resize: 'vertical',
          minHeight: '80px',
          ...(focused ? focusStyle : {}),
          ...(error ? { borderColor: '#f87171' } : {}),
        }}
      />
      {error && (
        <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#fca5a5' }}>{error}</p>
      )}
    </div>
  );
}

FormTextArea.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.string,
  rows: PropTypes.number,
};

// Checkbox
export function FormCheckbox({ label, checked, onChange, description }) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        marginBottom: '16px',
        cursor: 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          width: '20px',
          height: '20px',
          marginTop: '2px',
          accentColor: '#38bdf8',
          cursor: 'pointer',
        }}
      />
      <div>
        <span style={{ fontSize: '14px', color: '#e2e8f0', fontWeight: 500 }}>{label}</span>
        {description && (
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#94a3b8' }}>{description}</p>
        )}
      </div>
    </label>
  );
}

FormCheckbox.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  description: PropTypes.string,
};
